import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, X, Search, ExternalLink, Rocket, BadgePercent, Calculator, Briefcase, Pencil, LayoutGrid, List } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";
import PunchoutSandboxNotice, { isSandboxSupplier } from "@/components/PunchoutSandboxNotice";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const SORTS = [
  { value: "relevance", label: "Relevance" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "most_ordered", label: "Most ordered by us" },
];

export default function Catalog() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [brand, setBrand] = useState("");
  const [vendor, setVendor] = useState("");
  const [stock, setStock] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState("relevance");
  const [view, setView] = useState("grid"); // grid | list (Test Drive 2026-10-21)
  const [page, setPage] = useState(1);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const navigate = useNavigate();
  const [detailId, setDetailId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [costCodeId, setCostCodeId] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [addressHint, setAddressHint] = useState(null);
  const [deliveryAddressId, setDeliveryAddressId] = useState("");
  const [mode, setMode] = useState("pr");

  // Package 21 — job-first gate: shopping cannot start until a context is
  // pinned server-side. contextLoaded distinguishes "still checking" from
  // "confirmed none pinned" so the picker doesn't flash before the initial
  // fetch resolves.
  const [pinnedContext, setPinnedContext] = useState(null);
  const [contextLoaded, setContextLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadCart = () => api.get("/contractor/cart").then((r) => setCart(r.data));

  const loadContext = () =>
    api.get("/contractor/cart/context").then((r) => {
      setPinnedContext(r.data.context);
      setContextLoaded(true);
      if (!r.data.context) setPickerOpen(true);
    });

  const load = () => {
    const params = { sort, page, page_size: 24 };
    if (search.trim()) params.search = search.trim();
    if (category) params.category = category;
    if (subcategory) params.subcategory = subcategory;
    if (brand) params.brand = brand;
    if (vendor) params.vendor = vendor;
    if (stock) params.stock = stock;
    if (priceMax) params.price_max = Number(priceMax);
    api.get("/contractor/catalog", { params }).then((r) => setData(r.data));
  };

  useEffect(() => { load(); }, [category, subcategory, brand, vendor, stock, sort, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
  }, [search, priceMax]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/cost-codes").then((r) => setCostCodes(r.data));
    loadCart();
    loadContext();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Package 23 — delivery addresses filtered to the pinned job's own
  // profile, falling back to company addresses with a hint when it has
  // none; non-job contexts get the full company list, default first.
  useEffect(() => {
    if (!pinnedContext) return;
    if (pinnedContext.job_id) {
      // change_order contexts carry a job_id too (denormalized) — same
      // job-scoped filtering applies.
      api.get(`/contractor/jobs/${pinnedContext.job_id}/delivery-addresses`).then((r) => {
        setAddresses(r.data.addresses);
        setAddressHint(r.data.hint);
        if (r.data.preselect_id) setDeliveryAddressId(r.data.preselect_id);
      });
    } else {
      setAddressHint(null);
      api.get("/contractor/delivery-addresses").then((r) => {
        setAddresses(r.data);
        const def = r.data.find((a) => a.is_default);
        if (def) setDeliveryAddressId(def.id);
      });
    }
  }, [pinnedContext]);

  const addToCart = async (productId, qty = null) => {
    try {
      const product = data?.items.find((p) => p.id === productId);
      const useQty = qty ?? (product?.moq || 1);
      await api.post("/contractor/cart", { product_id: productId, qty: useQty });
      toast.success(`Added ${useQty} to cart`);
      loadCart();
    } catch (e) {
      if (e.response?.status === 400) {
        // The job-first gate is enforced server-side too (see
        // _get_pinned_cart_context) — this is what actually happens when
        // someone dismissed the picker and then tried to shop anyway.
        toast.error("Select a job (or office/warehouse) before shopping.");
        setPickerOpen(true);
      } else {
        toast.error(e.response?.data?.detail || "Failed to add to cart");
      }
    }
  };

  const removeFromCart = async (itemId) => {
    await api.delete(`/contractor/cart/${itemId}`);
    loadCart();
  };

  const checkout = async () => {
    if (!costCodeId) return toast.error("Select a cost code to apply to every line");
    if (!deliveryAddressId) return toast.error("Select a delivery address");
    try {
      await api.post("/contractor/cart/checkout", {
        cost_code_id: costCodeId, delivery_address_id: deliveryAddressId, mode,
      });
      toast.success(mode === "po" ? "Draft PO(s) created" : "Sent for approval");
      setCartOpen(false);
      loadCart();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send for approval failed");
    }
  };

  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.qty * i.unit_price, 0), [cart]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const contextLabel = useMemo(() => {
    if (!pinnedContext) return "";
    if (pinnedContext.type === "job") {
      const j = jobs.find((j) => j.id === pinnedContext.job_id);
      return j ? `${j.code} — ${j.name}` : "Job";
    }
    if (pinnedContext.type === "change_order") return "Change order";
    return "Non-job";
  }, [pinnedContext, jobs]);

  if (!data || !contextLoaded) return <div className="p-8 text-slate-400 text-sm">Loading catalog…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="catalog-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Catalog</h1>
          <p className="text-slate-500 text-sm mt-1">Your network vendors' products at your negotiated prices.</p>
        </div>
        <button onClick={() => setCartOpen(true)} data-testid="open-cart-button" className="relative flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
          <ShoppingCart size={15} /> Cart
          {cart.length > 0 && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{cart.length}</span>}
        </button>
      </div>

      {pinnedContext && (
        <button
          onClick={() => setPickerOpen(true)}
          data-testid="shopping-context-chip"
          className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-900 text-white text-[12.5px] font-semibold"
        >
          <Briefcase size={14} /> Shopping for: {contextLabel}
          <span className="inline-flex items-center gap-1 text-sky-300"><Pencil size={11} /> change</span>
        </button>
      )}

      <ContextPicker
        open={pickerOpen}
        firstTime={!pinnedContext}
        onOpenChange={setPickerOpen}
        jobs={jobs}
        cartCount={cart.length}
        onPinned={(ctx) => { setPinnedContext(ctx); setPickerOpen(false); loadCart(); }}
      />

      {pinnedContext && <PunchoutSection />}

      {!pinnedContext && (
        <button
          onClick={() => setPickerOpen(true)}
          className="mt-10 mx-auto flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm"
          data-testid="catalog-context-required"
        >
          <Briefcase size={14} /> Select a job (or office/warehouse) to start shopping
        </button>
      )}

      {pinnedContext && (
      <div className="flex flex-col lg:flex-row gap-6 mt-6">
        {/* Filter sidebar */}
        <div className="lg:w-56 shrink-0 space-y-4" data-testid="catalog-filters">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, SKU, brand…" data-testid="catalog-search-input"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13px]"
            />
          </div>
          <FilterSelect
            label="Vendor" value={vendor} onChange={(v) => { setVendor(v); setPage(1); }}
            options={(data.facets.vendors || []).map((v) => v.id)}
            labels={Object.fromEntries((data.facets.vendors || []).map((v) => [v.id, v.name]))}
            testId="filter-vendor"
          />
          <FilterSelect label="Category" value={category} onChange={(v) => { setCategory(v); setPage(1); }} options={data.facets.categories} testId="filter-category" />
          <FilterSelect label="Subcategory" value={subcategory} onChange={(v) => { setSubcategory(v); setPage(1); }} options={data.facets.subcategories} testId="filter-subcategory" />
          <FilterSelect label="Brand" value={brand} onChange={(v) => { setBrand(v); setPage(1); }} options={data.facets.brands} testId="filter-brand" />
          <FilterSelect label="Stock" value={stock} onChange={(v) => { setStock(v); setPage(1); }} options={["in_stock", "low", "out_of_stock"]} labels={{ in_stock: "In Stock", low: "Low", out_of_stock: "Out of Stock" }} testId="filter-stock" />
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Max Price</label>
            <input type="number" min="0" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="Any" data-testid="filter-price-max" className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px]" />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 min-w-0">
          {vendor && (
            <div className="flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-xl bg-sky-50 border border-sky-200" data-testid="catalog-active-vendor">
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-wide text-sky-600 font-bold">Viewing catalog</div>
                <div className="font-bold text-[15px] text-slate-900 truncate">{(data.facets.vendors || []).find((v) => v.id === vendor)?.name || "Vendor"}</div>
              </div>
              <button onClick={() => { setVendor(""); setPage(1); }} data-testid="catalog-clear-vendor" className="text-[12px] font-semibold text-sky-700 hover:underline shrink-0">
                Show all vendors
              </button>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-[12.5px] text-slate-500">{data.total} product{data.total === 1 ? "" : "s"}{!vendor && (data.facets.vendors || []).length > 1 ? ` from ${data.facets.vendors.length} vendors` : ""}</div>
            <div className="flex items-center gap-2">
              <SearchableSelect value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort-select" className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </SearchableSelect>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden" role="group" aria-label="View">
                {[["grid", LayoutGrid, "Grid"], ["list", List, "List"]].map(([v, Icon, label]) => (
                  <button key={v} type="button" onClick={() => setView(v)} title={label} aria-pressed={view === v} data-testid={`catalog-view-${v}`}
                    className={`h-9 w-9 flex items-center justify-center ${view === v ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                    <Icon size={15} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {view === "list" ? (
            <div className="overflow-x-auto border border-slate-200 rounded-xl" data-testid="catalog-list">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                    <th className="text-left py-2.5 px-3">Product</th><th className="text-left py-2.5">Vendor</th>
                    <th className="text-right py-2.5">Price</th><th className="text-left py-2.5 pl-4">Stock</th><th className="py-2.5 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={p.id} onClick={() => setDetailId(p.id)} className="cursor-pointer hover:bg-slate-50 border-b border-slate-50" data-testid={`product-row-${p.sku || p.id}`}>
                      <td className="py-2.5 px-3">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-[11px] text-slate-400 mono">{[p.sku, p.brand].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td className="py-2.5 text-slate-600">{p.vendor_name}</td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        <span className="mono font-semibold">{fmt(p.price)}</span>
                        <span className="text-[11px] text-slate-400">/{p.price_uom || p.uom}</span>
                        {p.your_price && <div className="text-[10px] font-bold text-emerald-700">Your price</div>}
                        {p.price_validity === "expired" && <div className="text-[10px] font-bold text-amber-700">Price expired</div>}
                      </td>
                      <td className="py-2.5 pl-4"><StatusBadge status={p.stock_status} /></td>
                      <td className="py-2.5 px-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); addToCart(p.id); }}
                          disabled={p.stock_status === "out_of_stock" && !p.special_order}
                          data-testid={`add-to-cart-list-${p.sku || p.id}`}
                          className="h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-[12px] font-semibold disabled:opacity-40 whitespace-nowrap"
                        >
                          Add {p.moq > 1 ? `(min ${p.moq})` : ""}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 && <tr><td colSpan={5} className="text-center py-14 text-slate-400 text-sm">No products match.</td></tr>}
                </tbody>
              </table>
            </div>
          ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.items.map((p) => (
              <div key={p.id} className="border border-slate-200 rounded-xl overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow" onClick={() => setDetailId(p.id)} data-testid={`product-card-${p.sku || p.id}`}>
                {(p.images?.[0]) && <img src={p.images[0]} alt={p.name} className="h-36 w-full object-cover" />}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5" data-testid={`vendor-tag-${p.sku || p.id}`}>{p.vendor_name}</span>
                    {p.source === "quickbooks" && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" title="Synced from this vendor's QuickBooks">QuickBooks</span>
                    )}
                    {p.brand && <span className="text-[11px] text-slate-400">{p.brand}</span>}
                  </div>
                  <div className="font-semibold text-[14px] mt-0.5">{p.name}</div>
                  {p.sku && <div className="text-[11px] text-slate-400 mono mt-0.5">{p.sku}</div>}
                  <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                    <div>
                      <span className="mono font-bold text-[15px]">{fmt(p.price)}</span>
                      <span className="text-[11px] text-slate-400">/{p.price_uom || p.uom}</span>
                      {p.your_price && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`your-price-${p.sku || p.id}`}>
                          <BadgePercent size={10} /> Your price
                        </span>
                      )}
                      {p.price_validity === "expired" && (
                        <span className="ml-1.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title="Contract price expired — confirm with the vendor" data-testid={`price-expired-${p.sku || p.id}`}>
                          Price expired
                        </span>
                      )}
                    </div>
                    <StatusBadge status={p.stock_status} />
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); addToCart(p.id); }}
                    disabled={p.stock_status === "out_of_stock" && !p.special_order}
                    data-testid={`add-to-cart-${p.sku || p.id}`}
                    className="mt-3 w-full h-9 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-[12.5px] font-semibold disabled:opacity-40"
                  >
                    Add {p.moq > 1 ? `(min ${p.moq})` : ""}
                  </button>
                </div>
              </div>
            ))}
            {data.items.length === 0 && <div className="col-span-full text-center py-14 text-slate-400 text-sm">No products match.</div>}
          </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6 text-[12.5px]">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Previous</button>
              <span className="text-slate-500">Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      </div>
      )}

      <ProductDetailDrawer productId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onAdd={addToCart} />

      {/* Cart sheet */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>Cart ({cart.length})</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-3">
            {cart.map((i) => (
              <div key={i.id} className="flex items-start justify-between gap-2 border-b border-slate-100 pb-3" data-testid={`cart-item-${i.id}`}>
                <div className="text-[13px]">
                  <div className="font-medium">{i.product_name}</div>
                  <div className="text-slate-500 text-[12px]">
                    {i.qty} {i.uom || "each"} × {fmt(i.unit_price)}
                    {i.your_price && <span className="ml-1 text-emerald-600 font-semibold">(your price)</span>}
                  </div>
                  <div className="text-[11px] text-slate-400">{i.vendor_name}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="mono text-[13px] font-semibold">{fmt(i.qty * i.unit_price)}</span>
                  <button onClick={() => removeFromCart(i.id)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                </div>
              </div>
            ))}
            {cart.length === 0 && <div className="text-slate-400 text-sm py-6 text-center">Cart is empty.</div>}
          </div>
          {cart.length > 0 && (
            <div className="mt-5 space-y-3">
              {/* Package 35 — the side cart is a summary only; the order is
                  reviewed and sent from the full-page Order Review. */}
              <div className="text-[12px] text-slate-500">Shopping for: <span className="font-semibold text-slate-700">{contextLabel}</span></div>
              <div className="flex justify-between text-[14px] font-bold"><span>Subtotal</span><span className="mono">{fmt(cartTotal)}</span></div>
              <button onClick={() => { setCartOpen(false); navigate("/contractor/order-review"); }} data-testid="review-order-button" className="w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-bold">
                Review order
              </button>
              <p className="text-[11px] text-slate-400 text-center">Review the full PO on its own page, then Send for Approval or save a Draft PO.</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, labels = {}, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{label}</label>
      <SearchableSelect value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]">
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{labels[o] || o}</option>)}
      </SearchableSelect>
    </div>
  );
}

const DELIVERY_LABELS = { vendor_delivers: "Vendor delivers", pickup_only: "Pickup only", freight: "Freight" };

const downloadDoc = (docId, fallbackName) =>
  api.get(`/documents/${docId}/download`, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: fallbackName });
    a.click();
    URL.revokeObjectURL(url);
  }).catch(() => toast.error("Could not download the document"));

function RelatedProductCard({ item, onView }) {
  return (
    <button onClick={() => onView(item.id)} data-testid={`related-product-${item.sku || item.id}`}
      className="w-full flex items-center gap-3 border border-slate-100 rounded-lg p-2.5 text-left hover:border-sky-200 hover:bg-sky-50/40">
      {item.image ? <img src={item.image} alt="" className="w-10 h-10 rounded-md object-cover" /> : <div className="w-10 h-10 rounded-md bg-slate-100" />}
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold truncate">{item.name}</div>
        <div className="text-[11px] text-slate-400 truncate">{item.vendor_name}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="mono text-[12.5px] font-bold">{fmt(item.price)}</div>
        <StatusBadge status={item.stock_status} />
      </div>
    </button>
  );
}

function ProductDetailDrawer({ productId, open, onOpenChange, onAdd }) {
  const [product, setProduct] = useState(null);
  const [imageIdx, setImageIdx] = useState(0);
  const [area, setArea] = useState("");
  const [depth, setDepth] = useState("");
  const [coverage, setCoverage] = useState(null);
  // Related-item navigation stays inside the drawer (Package 39).
  const [overrideId, setOverrideId] = useState(null);
  const viewId = overrideId || productId;

  useEffect(() => { setOverrideId(null); }, [open, productId]);
  useEffect(() => {
    if (!open || !viewId) return;
    setProduct(null); setImageIdx(0); setArea(""); setDepth(""); setCoverage(null);
    api.get(`/contractor/catalog/${viewId}`).then((r) => {
      setProduct(r.data);
      if (r.data.coverage_rate?.depth_in) setDepth(String(r.data.coverage_rate.depth_in));
    });
  }, [open, viewId]);

  const calculate = async () => {
    if (!area || Number(area) <= 0) return toast.error("Enter the area to cover");
    const params = { area: Number(area) };
    if (depth) params.depth_in = Number(depth);
    const { data } = await api.get(`/contractor/catalog/${viewId}/coverage`, { params });
    setCoverage(data);
  };

  if (!product && open) return (
    <Sheet open={open} onOpenChange={onOpenChange}><SheetContent side="right" className="w-full sm:max-w-lg"><div className="text-slate-400 text-sm mt-8">Loading…</div></SheetContent></Sheet>
  );
  if (!product) return null;

  const SPEC_ROWS = [
    ["SKU", product.sku], ["MPN", product.manufacturer_part_number], ["UPC", product.upc],
    ["Brand", product.brand], ["Manufacturer", product.manufacturer],
    ["Category", [product.category, product.subcategory].filter(Boolean).join(" / ")],
    ["Unit", product.uom], ["Package Qty", product.package_qty], ["Weight", product.weight ? `${product.weight} lb` : null],
    ["Dimensions", product.dimensions ? `${product.dimensions.l}×${product.dimensions.w}×${product.dimensions.h} ${product.dimensions.unit || ""}` : null],
    ["MOQ", product.moq > 1 ? product.moq : null], ["Order Increment", product.order_increment > 1 ? product.order_increment : null],
    ["Lead Time", product.lead_time_days ? `${product.lead_time_days} days` : null],
    ["Special Order", product.special_order ? "Yes" : null],
    ["Coverage", product.coverage_rate ? `${product.coverage_rate.value} ${product.coverage_rate.unit} per ${product.coverage_rate.per_unit}${product.coverage_rate.depth_in ? ` @ ${product.coverage_rate.depth_in}in depth` : ""}` : null],
    // Package 39 — logistics (workflow doc)
    ["Ships From", product.ships_from],
    ["Delivery", product.delivery_method ? DELIVERY_LABELS[product.delivery_method] || product.delivery_method : null],
    ["Freight Class", product.freight_class],
    ["Pallet Config", product.pallet_configuration],
    ["Price Valid", product.price_effective_date || product.price_expiry_date
      ? `${fmtDate(product.price_effective_date, "…")} – ${fmtDate(product.price_expiry_date, "…")}` : null],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="product-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            {product.name}
            {product.your_price && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"><BadgePercent size={10} /> Your price</span>}
          </SheetTitle>
        </SheetHeader>

        {product.images?.length > 0 && (
          <div className="mt-4">
            <img src={product.images[imageIdx]} alt={product.name} className="w-full h-56 object-cover rounded-xl border border-slate-100" />
            {product.images.length > 1 && (
              <div className="flex gap-2 mt-2">
                {product.images.map((url, i) => (
                  <img key={i} src={url} alt="" onClick={() => setImageIdx(i)} className={`w-12 h-12 rounded-lg object-cover cursor-pointer border-2 ${i === imageIdx ? "border-sky-500" : "border-transparent"}`} />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex items-baseline gap-2">
          <span className="mono font-extrabold text-[22px]">{fmt(product.price)}</span>
          <span className="text-[12px] text-slate-400">per {product.price_uom || product.uom}</span>
          <span className="ml-auto flex items-center gap-1.5">
            {product.hazmat && <span data-testid="hazmat-badge" className="ss-pill bg-amber-50 text-amber-700 border border-amber-200">Hazmat</span>}
            <StatusBadge status={product.stock_status} />
          </span>
        </div>
        <div className="text-[12px] text-slate-400">{product.vendor_name}</div>

        {product.price_validity === "expired" && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] text-amber-800" data-testid="price-expired-warning">
            This price expired {fmtDate(product.price_expiry_date)} — confirm current pricing with {product.vendor_name} before ordering.
          </div>
        )}
        {product.price_validity === "not_yet_effective" && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] text-amber-800">
            This price takes effect {fmtDate(product.price_effective_date)} — confirm what applies today with {product.vendor_name}.
          </div>
        )}

        {product.description && <p className="text-[13px] text-slate-600 mt-3 whitespace-pre-line">{product.description}</p>}

        {product.replacement && (
          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="replacement-banner">
            <div className="text-[12px] font-bold text-slate-600 mb-2">This item has been replaced by:</div>
            <RelatedProductCard item={product.replacement} onView={setOverrideId} />
          </div>
        )}

        {product.substitutes?.length > 0 && (
          <div className="mt-4" data-testid="substitutes-section">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1.5">
              {product.stock_status === "out_of_stock" ? "Out of stock — consider these substitutes" : "Substitute products"}
            </div>
            <div className="space-y-1.5">
              {product.substitutes.map((s) => <RelatedProductCard key={s.id} item={s} onView={setOverrideId} />)}
            </div>
          </div>
        )}

        {product.complementary?.length > 0 && (
          <div className="mt-4" data-testid="complementary-section">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1.5">Pairs well with</div>
            <div className="space-y-1.5">
              {product.complementary.map((s) => <RelatedProductCard key={s.id} item={s} onView={setOverrideId} />)}
            </div>
          </div>
        )}

        {product.coverage_rate && (
          <div className="mt-5 border border-sky-100 bg-sky-50/50 rounded-xl p-4" data-testid="coverage-calculator">
            <div className="flex items-center gap-1.5 text-[13px] font-bold text-sky-800"><Calculator size={14} /> Coverage Calculator</div>
            <div className="flex gap-2 mt-2.5">
              <input type="number" min="0" value={area} onChange={(e) => setArea(e.target.value)} placeholder={`Area (${product.coverage_rate.unit})`} data-testid="coverage-area-input" className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-[12.5px]" />
              <input type="number" min="0" step="0.5" value={depth} onChange={(e) => setDepth(e.target.value)} placeholder="Depth (in)" data-testid="coverage-depth-input" className="w-28 h-9 px-3 rounded-lg border border-slate-200 text-[12.5px]" />
              <button onClick={calculate} data-testid="coverage-calc-button" className="px-3.5 h-9 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12.5px] font-semibold">Calculate</button>
            </div>
            {coverage && (
              <div className="mt-3 text-[13px] text-sky-900" data-testid="coverage-result">
                You need <strong>{coverage.raw_qty}</strong> → suggested <strong>{coverage.suggested_qty} {product.uom}{coverage.suggested_qty === 1 ? "" : "s"}</strong>
                {" "}(rounded to the order increment{product.moq > 1 ? `, min ${product.moq}` : ""}) ≈ <strong>{fmt(coverage.estimated_cost)}</strong>
                <button
                  onClick={() => { onAdd(product.id, coverage.suggested_qty); onOpenChange(false); }}
                  data-testid="coverage-add-button"
                  className="block mt-2 px-3.5 h-9 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-[12.5px] font-semibold"
                >
                  Add {coverage.suggested_qty} to cart
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1.5">Specifications</div>
          <table className="w-full text-[12.5px]" data-testid="product-specs-table">
            <tbody>
              {SPEC_ROWS.map(([label, value]) => (
                <tr key={label} className="border-t border-slate-100">
                  <td className="py-1.5 text-slate-500 w-36">{label}</td>
                  <td className="py-1.5">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(product.spec_sheet_document_id || product.sds_document_id) && (
            <div className="flex gap-3 mt-2">
              {product.spec_sheet_document_id && (
                <button onClick={() => downloadDoc(product.spec_sheet_document_id, `${product.name} — spec sheet`)}
                  className="text-[12px] font-bold text-sky-600 hover:text-sky-700">Spec sheet (PDF)</button>
              )}
              {product.sds_document_id && (
                <button onClick={() => downloadDoc(product.sds_document_id, `${product.name} — SDS`)} data-testid="sds-link"
                  className="text-[12px] font-bold text-sky-600 hover:text-sky-700">Safety Data Sheet (SDS)</button>
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => { onAdd(product.id); onOpenChange(false); }}
          disabled={product.stock_status === "out_of_stock" && !product.special_order}
          data-testid="detail-add-to-cart"
          className="mt-5 w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-[13px] font-bold disabled:opacity-40"
        >
          Add to Cart{product.moq > 1 ? ` (min ${product.moq})` : ""}
        </button>
      </SheetContent>
    </Sheet>
  );
}

function PunchoutSection() {
  const [suppliers, setSuppliers] = useState([]);
  const [launchingId, setLaunchingId] = useState(null); // supplier id currently launching (button spinner)
  const [pendingSession, setPendingSession] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/contractor/punchout/suppliers").then((r) => setSuppliers(r.data.filter((s) => s.active))).catch(() => {});
  }, []);

  // Poll while a launched session is out shopping — the cart return
  // normally redirects that tab to the draft PR, this covers the case
  // where the user comes back to this tab first.
  useEffect(() => {
    if (!pendingSession) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/contractor/punchout/sessions/${pendingSession}`);
        if (data.status === "pr_created" && data.pr_id) {
          clearInterval(t);
          toast.success(`Cart returned from ${data.supplier_name} — assign cost codes and submit.`);
          setPendingSession(null);
          navigate(`/contractor/purchase-requests/${data.pr_id}?punchout=1`);
        }
        if (["expired", "confirmed", "ordered"].includes(data.status)) {
          clearInterval(t);
          setPendingSession(null);
        }
      } catch {
        clearInterval(t);
        setPendingSession(null);
      }
    }, 4000);
    return () => clearInterval(t);
  }, [pendingSession, navigate]);

  // Package 21: no per-launch job question — the supplier inherits
  // whichever context is already pinned on the cart session (the backend
  // resolves it server-side when context is omitted from the request).
  const launch = async (supplier) => {
    setLaunchingId(supplier.id);
    try {
      const { data } = await api.post(`/contractor/punchout/${supplier.id}/start`, {});
      // New tab rather than iframe: supplier shops set frame-busting and
      // restrictive CSP headers, and their checkout flows assume a
      // top-level browsing context — an iframe breaks on most real
      // suppliers. The tab returns to us via BrowserFormPost.
      window.open(data.start_page_url, "_blank", "noopener");
      setPendingSession(data.session_id);
      toast.success(`Shopping at ${supplier.name} — the cart will return here.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to launch punchout");
    } finally {
      setLaunchingId(null);
    }
  };

  if (suppliers.length === 0) return null;

  return (
    <div className="mt-5 border border-slate-200 rounded-xl p-4" data-testid="punchout-section">
      <div className="flex items-center gap-2 text-[13px] font-bold"><Rocket size={14} className="text-sky-500" /> Shop at Supplier (Punchout)</div>
      <div className="flex flex-wrap gap-2 mt-2.5">
        {suppliers.map((s) => (
          <button
            key={s.id} disabled={launchingId === s.id} onClick={() => launch(s)}
            data-testid={`punchout-launch-${s.id}`}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50 disabled:opacity-60"
          >
            <ExternalLink size={12} /> {launchingId === s.id ? "Launching…" : s.name}
            {isSandboxSupplier(s) && <span className="ss-pill ss-pill-amber">Sandbox</span>}
          </button>
        ))}
      </div>
      {suppliers.some(isSandboxSupplier) && <PunchoutSandboxNotice className="mt-2.5" />}
      {pendingSession && <div className="text-[12px] text-sky-600 mt-2 animate-pulse">Waiting for your cart to return…</div>}
    </div>
  );
}

// Package 21 — the job-first gate. Opens automatically on first visit to
// prompt for a context, but is always dismissable (close button, outside
// click, Escape) — the real enforcement is server-side (POST /cart 400s
// with no context pinned; see _get_pinned_cart_context), so trapping the
// user in a modal that also blocks navigating to any other page was an
// unnecessary UX dead end, not the actual gate. Reachable afterward via
// the "Shopping for: X [change]" chip, or the placeholder prompt when
// nothing's pinned. Mirrors the job/change-order/non-job picker pattern
// already used on the manual PR form and quick-log dialog.
function ContextPicker({ open, firstTime, onOpenChange, jobs, cartCount, onPinned }) {
  const [contextType, setContextType] = useState("job");
  const [jobId, setJobId] = useState("");
  const [coJobId, setCoJobId] = useState("");
  const [changeOrderId, setChangeOrderId] = useState("");
  const [changeOrders, setChangeOrders] = useState([]);
  const [nonJobContextId, setNonJobContextId] = useState("");
  const [contexts, setContexts] = useState([]);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/contractor/contexts").then((r) => setContexts(r.data));
  }, [open]);

  useEffect(() => {
    if (contextType !== "change_order" || !coJobId) { setChangeOrders([]); return; }
    api.get(`/contractor/jobs/${coJobId}/change-orders`).then((r) => setChangeOrders(r.data));
  }, [contextType, coJobId]);

  const selectContextType = (t) => { setContextType(t); setJobId(""); setCoJobId(""); setChangeOrderId(""); setNonJobContextId(""); };

  const buildContext = () => {
    if (contextType === "job") return jobId ? { type: "job", job_id: jobId } : null;
    if (contextType === "change_order") return changeOrderId ? { type: "change_order", change_order_id: changeOrderId } : null;
    return nonJobContextId ? { type: "non_job", context_id: nonJobContextId } : null;
  };

  const pin = async (confirmSwitch, clearCart) => {
    const context = buildContext();
    if (!context) return toast.error("Select a job or context");
    setSaving(true);
    try {
      const { data } = await api.put("/contractor/cart/context", { context, confirm_switch: confirmSwitch, clear_cart: clearCart });
      setConflict(false);
      onPinned(data.context);
    } catch (e) {
      if (e.response?.status === 409) {
        setConflict(true);
      } else {
        toast.error(e.response?.data?.detail || "Failed to set shopping context");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); setConflict(false); }}>
      <DialogContent data-testid="shopping-context-picker" showClose>
        <DialogHeader><DialogTitle>{firstTime ? "What are you shopping for?" : "Change what you're shopping for"}</DialogTitle></DialogHeader>
        <p className="text-[12.5px] text-slate-500">Pick a job, change order, or office/warehouse before browsing the catalog — every item you add carries this context.</p>

        {!conflict ? (
          <>
            <div className="flex gap-2 mt-2">
              {[["job", "Job"], ["change_order", "Change Order"], ["non_job", "Non-Job"]].map(([t, label]) => (
                <button
                  key={t} type="button" onClick={() => selectContextType(t)} data-testid={`shopping-context-type-${t}`}
                  className={`px-3.5 py-2 rounded-lg text-[12.5px] font-semibold ${contextType === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {contextType === "job" && (
              <SearchableSelect value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="shopping-context-job-select" className="mt-2.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                <option value="">Select job…</option>
                {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
              </SearchableSelect>
            )}
            {contextType === "change_order" && (
              <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <SearchableSelect value={coJobId} onChange={(e) => { setCoJobId(e.target.value); setChangeOrderId(""); }} data-testid="shopping-context-co-job-select" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  <option value="">Select job…</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
                </SearchableSelect>
                <SearchableSelect value={changeOrderId} onChange={(e) => setChangeOrderId(e.target.value)} disabled={!coJobId} data-testid="shopping-context-change-order-select" className="h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
                  <option value="">Select change order…</option>
                  {changeOrders.map((co) => <option key={co.id} value={co.id}>{co.code} — {co.name}</option>)}
                </SearchableSelect>
              </div>
            )}
            {contextType === "non_job" && (
              <SearchableSelect value={nonJobContextId} onChange={(e) => setNonJobContextId(e.target.value)} data-testid="shopping-context-select" className="mt-2.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                <option value="">Select context…</option>
                {contexts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SearchableSelect>
            )}
            <button disabled={saving} onClick={() => pin(false, false)} data-testid="shopping-context-confirm" className="mt-4 w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-bold disabled:opacity-60">
              {saving ? "Saving…" : "Start Shopping"}
            </button>
          </>
        ) : (
          <div className="mt-2 space-y-3" data-testid="shopping-context-conflict">
            <p className="text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              Your cart has {cartCount} item{cartCount === 1 ? "" : "s"} assigned to a different job — switch and keep items, or clear the cart?
            </p>
            <div className="flex gap-2">
              <button disabled={saving} onClick={() => pin(true, false)} data-testid="shopping-context-keep-items" className="flex-1 h-10 rounded-lg border border-slate-200 text-[12.5px] font-semibold">Keep items</button>
              <button disabled={saving} onClick={() => pin(true, true)} data-testid="shopping-context-clear-cart" className="flex-1 h-10 rounded-lg border border-red-200 text-red-600 text-[12.5px] font-semibold">Clear cart</button>
            </div>
            <button onClick={() => setConflict(false)} className="text-[12px] text-slate-500">Cancel</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
